import React from 'react';
import { withRouter } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Pagination } from 'ats/components/pagination/Pagination';
import DrawerContainer from 'ats/components/drawer/DrawerContainer';
import { drawerStore } from 'ats/components/drawer/DrawerStore';
import FilterBar from 'ats/components/filter-bar/FilterBar';
import FilterDrawer from 'ats/components/filter-drawer/FilterDrawer';
import { buildAppliedFilterItems } from 'ats/components/filter-drawer/buildAppliedFilterItems';
import { ListStore } from 'shared/stores/ListStore';
import { clientStore } from './ClientStore';
import ClientsTable from './ClientsTable';
import { ClientCreate } from './components/ClientCreate';
import ClientProfile from './ClientProfile';
import EmptyState from 'ats/components/empty-state/EmptyState';
import { EMPTY_STATE_ICONS } from 'ats/components/empty-state/emptyStateIcons';
import ViewsDropdown from 'ats/components/views-dropdown/ViewsDropdown';
import { ListViewController } from 'ats/components/views-dropdown/ListViewController';
import { LIST_VIEW_SECTIONS } from 'ats/components/views-dropdown/listViewSections';
import { attachListProfileDrawerHandlers } from 'ats/utils/openEntityProfile';
import { industryStore } from 'components/settings/industries/IndustryStore';

@withRouter
@observer
export class Clients extends React.Component {
	state = {
		searchFilter: null,
		activeClientId: null,
		industryFilter: null,
		sizeFilter: null,
		accountManagerFilter: null,
		primaryContactFilter: null,
		showOnlyWithOpenJobsFilter: false,
		visibleColumns: clientStore.defaultTableColumnsOptions
	};

	constructor(props) {
		super(props);
		this.listStore = new ListStore(clientStore, {
			entityName: 'Client',
			createModalTitle: 'New Client',
			createModalPosition: 'center',
			createModalSize: '2xl',
			createModal: <ClientCreate handleSuccess={this.handleCreateSuccess} />
		});
		this.listViewController = new ListViewController(
			LIST_VIEW_SECTIONS.CLIENTS,
			this
		);
		this.listStore.beginLoading();
	}

	async componentDidMount() {
		await industryStore.loadIndustries();
		await this.listViewController.initialize();

		const clientIdFromUrl = this.props.match?.params?.id;
		if (clientIdFromUrl) {
			this.openProfileById(clientIdFromUrl);
		}
	}

	componentWillUnmount() {
		this.listViewController.cancel();
		this.listStore.store.resetQuery();

		if (drawerStore.isOpen) {
			drawerStore.closeDrawer();
		}
	}

	componentDidUpdate(prevProps) {
		const prevId = prevProps.match?.params?.id;
		const nextId = this.props.match?.params?.id;

		if (prevId === nextId) return;

		if (nextId) {
			this.openProfileById(nextId);
		} else {
			if (
				drawerStore.isOpen &&
				drawerStore.drawerId === drawerStore.DRAWER_IDS.CLIENT_PROFILE
			) {
				drawerStore.closeDrawer();
			}
			this.setState({ activeClientId: null });
		}
	}

	handleCreateSuccess = result => {
		this.listStore.search();
		if (result?.data?.id) {
			this.props.history.push(`${this.getBaseUrl()}/${result.data.id}`);
		}
	};

	handlePaginationChange = value => {
		this.listStore.handlePaginationChange(
			value,
			this.listStore.store.query.pagination.limit
		);
	};

	getBaseUrl = () => {
		const url = this.props.match?.url || '';
		const id = this.props.match?.params?.id;
		if (!id) return url;
		return url.replace(new RegExp(`/${id}$`), '');
	};

	openProfileById = clientId => {
		this.setState({ activeClientId: clientId });

		const onClose = () => {
			this.setState({ activeClientId: null });
			drawerStore.closeDrawer();
			this.props.history.push(this.getBaseUrl());
		};
		const onSuccess = () => this.listStore.search();

		if (
			attachListProfileDrawerHandlers({
				drawerId: drawerStore.DRAWER_IDS.CLIENT_PROFILE,
				idKey: 'clientId',
				entityId: clientId,
				onClose,
				onSuccess
			})
		) {
			return;
		}

		drawerStore.openDrawer(
			{
				containerId: 'clients',
				drawerId: drawerStore.DRAWER_IDS.CLIENT_PROFILE,
				showHeader: false,
				props: {
					clientId,
					onSuccess,
					onClose
				}
			},
			ClientProfile
		);
	};

	openProfile = rowData => {
		const baseUrl = this.getBaseUrl();
		this.props.history.push(`${baseUrl}/${rowData.id}`);
		this.openProfileById(rowData.id);
	};

	getBarFilters = () => {
		return [
			{
				type: 'search',
				label: 'Search client name',
				name: 'search',
				onKeyPress: newValue => {
					this.setState({
						searchFilter: newValue
					});
				},
				onChange: newValue => {
					this.listStore.store.setQuery('text', newValue);
					this.handlePaginationChange(1);
				},
				value: this.state.searchFilter
			},
			{
				type: 'switch',
				label: 'Open Jobs',
				activeLabel: 'Open Jobs',
				inactiveLabel: 'Open Jobs',
				id: 'show-only-with-open-jobs-switch',
				onChange: newValue => {
					this.setState({ showOnlyWithOpenJobsFilter: newValue }, () => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					});
					this.listStore.store.setQuery('showOnlyWithOpenJobs', newValue);
					this.handlePaginationChange(1);
				},
				value: this.state.showOnlyWithOpenJobsFilter
			}
		];
	};

	getDrawerFilters = () => {
		return [
			{
				type: 'multi-dropdown',
				label: 'Industry',
				name: 'industry',
				onChange: newValue => {
					this.setState(
						{
							industryFilter: newValue
						},
						() => {
							drawerStore.updateDrawerProps({
								filters: this.getDrawerFilters()
							});
						}
					);

					this.listStore.store.setQuery(
						'industry',
						newValue.map(v => v.value)
					);
					this.handlePaginationChange(1);
				},
				value: this.state.industryFilter,
				options: clientStore.industryOptions
			},
			{
				type: 'number',
				label: 'Size',
				name: 'size',
				onKeyPress: newValue => {
					this.setState(
						{
							sizeFilter: newValue
						},
						() => {
							drawerStore.updateDrawerProps({
								filters: this.getDrawerFilters()
							});
						}
					);
				},
				onChange: newValue => {
					this.listStore.store.setQuery('size', newValue);
					this.handlePaginationChange(1);
				},
				value: this.state.sizeFilter
			},
			{
				type: 'user-dropdown',
				label: 'Account Manager',
				isMulti: true,
				role: 'Account Manager',
				minSearchChars: 0,
				isClearable: true,
				onChange: newValue => {
					this.setState(
						{
							accountManagerFilter: newValue
						},
						() => {
							drawerStore.updateDrawerProps({
								filters: this.getDrawerFilters()
							});
						}
					);
					this.listStore.store.setQuery(
						'accountManager',
						newValue.map(h => h.id)
					);
					this.handlePaginationChange(1);
				},
				value: this.state.accountManagerFilter
			},
			{
				type: 'text',
				label: 'Primary Contact',
				name: 'primaryContact',
				onKeyPress: newValue => {
					this.setState(
						{
							primaryContactFilter: newValue
						},
						() => {
							drawerStore.updateDrawerProps({
								filters: this.getDrawerFilters()
							});
						}
					);
				},
				onChange: newValue => {
					this.listStore.store.setQuery('primaryContact', newValue);
					this.handlePaginationChange(1);
				},
				value: this.state.primaryContactFilter
			}
		];
	};

	getAppliedFilters = () =>
		buildAppliedFilterItems([
			...this.getBarFilters(),
			...this.getDrawerFilters()
		]);

	renderClientsEmptyState = () => {
		const hasFilters = this.getAppliedFilters().length > 0;

		return (
			<EmptyState
				icon={
					hasFilters
						? EMPTY_STATE_ICONS.noResultsClients
						: EMPTY_STATE_ICONS.client
				}
				iconAlt={hasFilters ? 'No matching clients' : 'No clients yet'}
				message={
					hasFilters
						? 'No clients match your search criteria.'
						: 'No clients yet.'
				}
				detail={
					hasFilters
						? 'Try adjusting your filters or search terms to see more results.'
						: 'Create a client to get started.'
				}
				actionText={hasFilters ? 'Clear filters' : 'New Client'}
				onActionClick={
					hasFilters
						? this.resetDrawerFilters
						: () => this.listStore.handleCreate()
				}
			/>
		);
	};

	resetDrawerFilters = () => {
		this.setState(
			{
				searchFilter: '',
				industryFilter: null,
				sizeFilter: '',
				accountManagerFilter: null,
				primaryContactFilter: '',
				showOnlyWithOpenJobsFilter: false
			},
			() => {
				drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
			}
		);

		this.listStore.store.resetQuery();
		this.listStore.store.setQuery('sort', { column: 'createdAt', asc: false });
		this.handlePaginationChange(1);
	};

	render() {
		const isProfileOpen =
			drawerStore.isOpen &&
			[
				drawerStore.DRAWER_IDS.CLIENT_PROFILE,
				drawerStore.DRAWER_IDS.JOB_POSITION_PROFILE,
				drawerStore.DRAWER_IDS.APPLICANT_PROFILE
			].includes(drawerStore.drawerId);

		const isFilterDrawerOpen =
			drawerStore.isOpen &&
			drawerStore.drawerId === drawerStore.DRAWER_IDS.FILTER_DRAWER;

		return (
			<div className="Clients">
				<div className="header-container">
					<h2>Clients</h2>
					<ViewsDropdown
						section={LIST_VIEW_SECTIONS.CLIENTS}
						controller={this.listViewController}
						viewStateKey={this.listViewController.getViewStateKey()}
						isModified={this.listViewController.isModified()}
					/>
					<FilterBar
						filters={this.getBarFilters()}
						appliedFilters={this.getAppliedFilters()}
						onShowMore={() => {
							if (!isFilterDrawerOpen) {
								drawerStore.openDrawer(
									{
										containerId: 'clients',
										drawerId: drawerStore.DRAWER_IDS.FILTER_DRAWER,
										customHeader: (
											<>
												<button
													className="filter-drawer-back-btn"
													onClick={() => drawerStore.closeDrawer()}>
													<img
														src="/ats/icons/arrow-right-purple-icon.svg"
														alt=""
													/>
												</button>
												<h3>Filters</h3>
											</>
										),
										props: {
											filters: this.getDrawerFilters(),
											getAppliedFilters: this.getAppliedFilters,
											onReset: this.resetDrawerFilters
										}
									},
									FilterDrawer
								);
							} else {
								drawerStore.closeDrawer();
							}
						}}
					/>
					<button
						type="button"
						className="ats-button-primary"
						onClick={() => this.listStore.handleCreate()}>
						New Client
					</button>
				</div>

				<main>
					<div
						className={`clients-wrapper${
							isProfileOpen ? ' is-profile-open' : ''
						}`}>
						<div className="clients-container">
							<div className="ats-table-wrapper">
								<ClientsTable
									data={this.listStore.data}
									sort={this.listStore.store.query.sort}
									onSort={this.listStore.handleSort}
									onSuccess={() => this.listStore.search()}
									onClickRow={this.openProfile}
									activeRowId={this.state.activeClientId}
									visibleColumns={this.state.visibleColumns}
									onVisibleColumnsChange={visibleColumns =>
										this.setState({ visibleColumns })
									}
									loading={this.listStore.loading}
									isEmpty={
										!this.listStore.loading && this.listStore.data.length === 0
									}
									emptyState={this.renderClientsEmptyState()}
								/>
							</div>
						</div>
						<Pagination
							loading={this.listStore.loading}
							pagination={this.listStore.store.query.pagination}
							totalResults={this.listStore.store.query.pagination.totalResults}
							onPaginationChange={this.handlePaginationChange}
						/>
					</div>
					<DrawerContainer id="clients" />
				</main>
			</div>
		);
	}
}
